import type { ClassNameProp } from '@ps/web-shared-components';
import { get, isUndefined } from 'lodash';
import type { ReactElement, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import styles from './LazyImage.scss';

export interface Props extends ClassNameProp {
  alt?: string;
  fallback?: React.ReactNode;
  loader?: React.ReactNode;
  src: string;
  visible?: boolean;
}

enum Status {
  PRISTINE,
  PENDING,
  SUCCESS,
  FAILURE
}

const cache: Record<string, { observable: Observable<unknown>; status: Status }> = {};

const load = (src: string) => {
  if (!cache[src]) {
    cache[src] = {
      observable: new Observable((subscriber) => {
        const imgElem = new Image();
        imgElem.onload = () => {
          cache[src].status = Status.SUCCESS;
          subscriber.complete();
        };
        imgElem.onerror = () => {
          delete cache[src];
          subscriber.error();
        };
        imgElem.src = src;
      }).pipe(shareReplay({ bufferSize: 1, refCount: false })),
      status: Status.PRISTINE
    };
  }
  return cache[src];
};

const LazyImage: React.FC<Props> = ({ visible, src, fallback, loader, ...rest }) => {
  const [status, setStatus] = useState(get(cache, [src, 'status'], Status.PRISTINE));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if ((isVisible || visible) && [Status.PRISTINE, Status.PENDING].includes(status)) {
      setStatus(Status.PENDING);
      const subscription = load(src).observable.subscribe({
        complete: () => setStatus(Status.SUCCESS),
        error: () => setStatus(Status.FAILURE)
      });
      return () => subscription.unsubscribe();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isVisible, src]);

  let children: ReactNode;
  switch (status) {
    case Status.SUCCESS:
      // eslint-disable-next-line jsx-a11y/alt-text
      return <img src={src} {...rest} />;
    case Status.PRISTINE:
    case Status.PENDING:
      children = loader || fallback;
      break;
    case Status.FAILURE:
      children = fallback;
      break;
  }

  return isUndefined(visible) ? (
    <VisibilitySensor onChange={setIsVisible} partialVisibility delayedCall>
      {children}
    </VisibilitySensor>
  ) : (
    (children as ReactElement)
  );
};

LazyImage.defaultProps = {
  loader: <div className={styles.loader} />,
  fallback: <img width="1px" height="1px" alt="" />
};

export default LazyImage;
